import {
  getLogger,
  type BeforeReloadCallback,
  type BeforeReloadEvent,
  type BeforeReloadOptions,
  type ShellReloadReason,
} from '@abax/platform';

// Logs via the platform accessor rather than importing `@/services/logging`
// directly. That module imports the store, and the store imports helpers that
// need to tear the page down, so a direct import would make this service
// unreachable from those helpers without closing a require cycle. `getLogger()`
// resolves the same shell logger off `window` at call time (console before the
// shell API is installed), so it must be called per use, never cached.

interface Registration {
  callback: BeforeReloadCallback;
  owner: string;
}

const registrations = new Set<Registration>();

/**
 * The hook phase for this document. Teardowns can be raced (the version
 * countdown expiring while the user presses Update, access being revoked
 * mid-countdown) and an MFE must not be asked to flush twice, so the first
 * caller owns the run and later callers await the same promise.
 *
 * This latches the HOOKS only. The navigation is the caller's, deliberately, so
 * that a later cause is never dropped in favour of the one that got here first.
 */
let hookPhase: Promise<void> | null = null;

export function registerBeforeReload(
  callback: BeforeReloadCallback,
  options: BeforeReloadOptions = {},
): () => void {
  const owner = options.owner ?? 'anonymous';

  if (hookPhase !== null) {
    getLogger().warn('onBeforeReload: registered after teardown started, hook will not run', {
      owner,
    });
    return () => undefined;
  }

  const registration: Registration = { callback, owner };
  registrations.add(registration);
  return () => {
    registrations.delete(registration);
  };
}

/**
 * Isolates one consumer callback. A synchronous throw and a rejected promise are
 * the same failure from the shell's side: log it and carry on. The callback body
 * runs synchronously up to its first await, so a hook doing only synchronous
 * work completes even on a zero budget.
 *
 * `owner` is a context field. It does not set the Datadog service tag, which
 * `beforeSend` derives from the current URL, so a hook belonging to an MFE the
 * user has already navigated away from is filed under the MFE now on screen.
 */
function invokeHook(registration: Registration, event: BeforeReloadEvent): Promise<void> {
  const { callback, owner } = registration;
  const onFailure = (error: unknown) => {
    getLogger().error(
      'onBeforeReload hook failed',
      { owner, reason: event.reason },
      error instanceof Error ? error : new Error(String(error)),
    );
  };

  try {
    return Promise.resolve(callback(event)).catch(onFailure);
  } catch (error) {
    onFailure(error);
    return Promise.resolve();
  }
}

/**
 * Invokes every registered hook for one teardown.
 *
 * Hooks run concurrently, so one slow hook cannot spend another's share of the
 * budget, and the returned promise settles at the budget even if a hook never
 * does. It never rejects: the caller tears the page down regardless.
 *
 * `budgetMs <= 0` means the shell is not waiting. Hooks are still invoked, with
 * an already-aborted signal, so synchronous work lands and async work knows not
 * to start. Nothing is awaited before returning, which lets the caller navigate
 * in the same tick.
 */
function dispatchHooks(reason: ShellReloadReason, budgetMs: number): Promise<void> {
  const pending = Array.from(registrations);
  if (pending.length === 0) return Promise.resolve();

  const controller = new AbortController();
  const event: BeforeReloadEvent = { reason, signal: controller.signal };

  if (budgetMs <= 0) {
    controller.abort();
    pending.forEach((registration) => void invokeHook(registration, event));
    return Promise.resolve();
  }

  const outstanding = new Set(pending);
  const settled = Promise.allSettled(
    pending.map((registration) =>
      invokeHook(registration, event).finally(() => outstanding.delete(registration)),
    ),
  ).then(() => undefined);

  let budgetTimer: ReturnType<typeof setTimeout>;
  const budget = new Promise<void>((resolve) => {
    budgetTimer = setTimeout(resolve, budgetMs);
  });

  return Promise.race([settled, budget]).then(() => {
    clearTimeout(budgetTimer);
    if (outstanding.size > 0) {
      getLogger().warn('onBeforeReload hooks exceeded the reload budget', {
        reason,
        budgetMs,
        owners: Array.from(outstanding, (registration) => registration.owner),
      });
    }
    // Tells any still-running hook the shell has stopped waiting.
    controller.abort();
  });
}

/**
 * Runs the hook phase, once per document. The first caller owns the run; a later
 * teardown gets that same promise back rather than a second round of callbacks,
 * so an MFE is never asked to flush twice.
 *
 * A consequence worth stating, because it reads like a bug: once the hooks have
 * run, a later cause does not wait for its own budget. There is nothing left to
 * wait for, since the callbacks will not run again, and holding the page anyway
 * would be a delay that buys the user nothing. It also means the first cause
 * decides the budget the hooks actually get, so a no-wait cause arriving first
 * gives every hook an already-aborted signal.
 */
export function runBeforeReloadHooks(reason: ShellReloadReason, budgetMs: number): Promise<void> {
  if (hookPhase !== null) return hookPhase;
  hookPhase = dispatchHooks(reason, budgetMs);
  return hookPhase;
}
